import {
  CLAIM_STATUS,
  REQUEST_TYPE,
  RETURN_DELIVERED_TRACKING_STATUS,
  RETURN_STAGE,
} from "../constants";
import type { Claim, ReturnLabel } from "../types";

export type ReturnStage = (typeof RETURN_STAGE)[keyof typeof RETURN_STAGE];

const RETURN_REQUEST_TYPES: string[] = [
  REQUEST_TYPE.RETURN,
  REQUEST_TYPE.EXCHANGE,
];

export const isReturnRequest = (claim: Claim) =>
  RETURN_REQUEST_TYPES.includes(claim.request_type ?? "");

export const getActiveReturnLabel = (claim: Claim): ReturnLabel | null => {
  const labels = claim.return_labels ?? [];
  return labels[labels.length - 1] ?? null;
};

const isReturnResolved = (claim: Claim) =>
  claim.claim_status === CLAIM_STATUS.APPROVED ||
  claim.claim_status === CLAIM_STATUS.CLOSED;

const isLabelDelivered = (label: ReturnLabel) =>
  Boolean(label.delivered_at) ||
  label.tracking_status?.toLowerCase() === RETURN_DELIVERED_TRACKING_STATUS;

export const getReturnStage = (claim: Claim): ReturnStage => {
  if (claim.claim_status === CLAIM_STATUS.DENIED) {
    return RETURN_STAGE.DENIED;
  }
  if (isReturnResolved(claim)) {
    return RETURN_STAGE.RESOLVED;
  }

  const label = getActiveReturnLabel(claim);
  if (!label) {
    return RETURN_STAGE.REQUESTED;
  }
  if (isLabelDelivered(label)) {
    return RETURN_STAGE.RECEIVED;
  }
  return label.tracking_status
    ? RETURN_STAGE.IN_TRANSIT
    : RETURN_STAGE.LABEL_READY;
};

const LABEL_VISIBLE_STAGES: ReturnStage[] = [
  RETURN_STAGE.LABEL_READY,
  RETURN_STAGE.IN_TRANSIT,
];

export const getVisibleReturnLabel = (claim: Claim): ReturnLabel | null =>
  isReturnRequest(claim) && LABEL_VISIBLE_STAGES.includes(getReturnStage(claim))
    ? getActiveReturnLabel(claim)
    : null;
